/**
 * The per-line Tax badge, in plain words.
 *
 * The server sends three things on every quote and invoice line:
 *   taxed      — what the tax engine actually does with the line
 *   taxable    — the office's own toggle on the line (null = none)
 *   tax_reason — why: 'residential_labor' | 'by_hand' | 'item' | 'rule'
 *
 * Homes: Texas does not tax labor to repair a home, only the parts
 * (34 TAC §3.291, Comptroller Pub. 94-116). Those lines read
 * "Not taxed: residential labor" and cannot be switched to taxed.
 */
export type LineTaxReason = "residential_labor" | "by_hand" | "item" | "rule";

export interface LineTaxBadge {
    /** Short words on the badge. */
    label: string;
    /** Hover words. */
    title: string;
    /** Amber when the line is taxed. */
    taxed: boolean;
    /** False when the office cannot flip it (residential labor). */
    canToggle: boolean;
}

export function lineTaxBadge(line: {
    taxed?: boolean | null;
    taxable?: boolean | null;
    tax_reason?: string | null;
}): LineTaxBadge {
    const reason = line.tax_reason ?? null;
    // Older payloads without `taxed` fall back to the office toggle, then taxed.
    const taxed = line.taxed ?? line.taxable ?? true;
    if (reason === "residential_labor") {
        return {
            label: "Not taxed: residential labor",
            title: "This customer is a home. Texas does not tax labor on a home, only the parts.",
            taxed: false,
            canToggle: false,
        };
    }
    if (reason === "by_hand") {
        return {
            label: taxed ? "Taxed (set by hand)" : "Not taxed (set by hand)",
            title: "Someone set this line by hand. Click to switch it.",
            taxed,
            canToggle: true,
        };
    }
    return {
        label: taxed ? "Taxed" : "Not taxed",
        title: taxed
            ? "Taxed by the price book. Click to mark this line not taxed."
            : "Tests and city or admin fees are never taxed. Click to mark this line taxed.",
        taxed,
        canToggle: true,
    };
}
